import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import { QUERY_KEYS } from './queryKeys';

export interface PartsRequisition {
    id: number;
    equipment_code: string;
    date: string;
    requester_name?: string;
    priority: 'urgent' | 'high' | 'medium' | 'low';
    status: 'draft' | 'submitted' | 'approved' | 'rejected';
    status_display: string;
    total_amount: number;
}

const fetchPartsRequisitions = async (): Promise<PartsRequisition[]> => {
    const { data } = await api.get(API_ROUTES.partsRequisitions);
    return data;
};

export const usePartsRequisitionsList = () => {
    return useQuery({
        queryKey: QUERY_KEYS.partsRequisitions.all,
        queryFn: fetchPartsRequisitions,
    });
};

const createPartsRequisition = async (data: any) => {
    const { data: response } = await api.post(API_ROUTES.partsRequisitions, data);
    return response;
};

export const useCreatePartsRequisition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createPartsRequisition,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.partsRequisitions.all });
        },
    });
};

const fetchPartsRequisitionDetail = async (id: string | number) => {
    const { data } = await api.get(API_ROUTES.partsRequisitionDetail(id));
    return data;
};

export const usePartsRequisitionDetail = (id: string | number) => {
    return useQuery({
        queryKey: QUERY_KEYS.partsRequisitions.detail(id),
        queryFn: () => fetchPartsRequisitionDetail(id),
        enabled: !!id,
    });
};

const updatePartsRequisition = async ({ id, data }: { id: string | number; data: any }) => {
    const { data: response } = await api.put(API_ROUTES.partsRequisitionDetail(id), data);
    return response;
};

export const useUpdatePartsRequisition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updatePartsRequisition,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.partsRequisitions.all });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.partsRequisitions.detail(variables.id) });
        },
    });
};
